/**
 * Based on code example:
 * https://material-ui.com/components/tables/#sorting-amp-selecting
 */

import React, { ReactNode, useMemo, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';

import { IOrderProperty, IOrderState, orderBy, useOrder } from 'utils/ordering';

import IColumnDefinition from '../Table/IColumnDefinition';

interface ISelection {
  isSelected: (id: string) => boolean;
  selectOnly: (id: string) => void;
  multi?: {
    someSelected: boolean;
    allSelected: boolean;
    toggle: (id: string) => void;
    selectAll: () => void;
    deselectAll: () => void;
  };
}

interface HasId {
  id: string;
}

interface EnhancedTableProps<IItem> {
  selection?: ISelection;
  columns: IColumnDefinition<IItem>[];
  order: void | IOrderState<IItem>;
  toggleOrderOf: (property: IOrderProperty<IItem>) => void;
  disableSorting?: boolean;
}

function EnhancedTableHead<IItem extends HasId>(props: EnhancedTableProps<IItem>) {
  const { columns, order, toggleOrderOf, selection, disableSorting } = props;

  function renderMultiCheckbox() {
    if (!selection || !selection.multi) {
      return;
    }
    const { someSelected, allSelected, selectAll, deselectAll } = selection.multi;

    return (
      <TableCell padding="checkbox">
        <Checkbox
          checked={someSelected}
          indeterminate={someSelected && !allSelected}
          onClick={someSelected ? deselectAll : selectAll}
        />
      </TableCell>
    );
  }

  return (
    <TableHead>
      <TableRow>
        {renderMultiCheckbox()}
        {columns.map((column, index) => {
          const showCheckbox = selection && selection.multi;
          const isAfterCheckbox = showCheckbox && index === 0;

          const { orderProperty } = column;

          if (orderProperty && !disableSorting) {
            return (
              <TableCell
                key={String(column.key)}
                align="left"
                padding={isAfterCheckbox ? 'none' : 'normal'}
                sortDirection={order && order.key === column.key ? order.direction : false}
              >
                <TableSortLabel
                  active={order ? order.key === column.key : false}
                  direction={(order && order.key === column.key && order.direction) || 'asc'}
                  onClick={() => toggleOrderOf(orderProperty)}
                >
                  {column.header}
                </TableSortLabel>
              </TableCell>
            );
          } else {
            return (
              <TableCell key={String(column.key)} align="left" padding={isAfterCheckbox ? 'none' : 'normal'}>
                {column.header}
              </TableCell>
            );
          }
        })}
      </TableRow>
    </TableHead>
  );
}

export const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100];

export interface IServerPagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

interface IProps<IItem> {
  columns: IColumnDefinition<IItem>[];
  items: IItem[];

  selection?: ISelection;
  defaultPageSize?: number;
  serverPagination?: IServerPagination;
}

export default function EnhancedTable<IItem extends HasId>(props: IProps<IItem>) {
  const { columns, selection, serverPagination } = props;
  const [order, toggleOrderOf] = useOrder<IItem>();

  const items = useMemo(() => (!order ? props.items : props.items.slice(0).sort(orderBy(order))), [order, props.items]);

  const [localPage, setLocalPage] = React.useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = React.useState(props.defaultPageSize ?? 25);

  const isServerMode = !!serverPagination;
  // Server pagination uses 1-based pages; MUI uses 0-based
  const page = isServerMode ? serverPagination.page - 1 : localPage;
  const rowsPerPage = isServerMode ? serverPagination.pageSize : localRowsPerPage;
  const totalCount = isServerMode ? serverPagination.total : items.length;

  const rowHeightPx = 33;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  function resetScroll() {
    const node = scrollContainerRef.current;
    if (node) {
      node.scrollTop = 0;
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    if (isServerMode) {
      serverPagination.onPageChange(newPage + 1); // convert to 1-based
    } else {
      setLocalPage(newPage);
    }
    resetScroll();
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    if (isServerMode) {
      serverPagination.onPageSizeChange(newSize);
    } else {
      setLocalRowsPerPage(newSize);
      setLocalPage(0);
    }
  };

  // In server mode, items are already the correct page slice
  const displayItems = isServerMode ? items : items.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const emptyRows = rowsPerPage - displayItems.length;

  return (
    <div style={{ width: '100%', border: '1px solid #c4c4c4' }}>
      <TableContainer
        style={{
          maxHeight: 440 - (440 % rowHeightPx) + 5 /* + 5 because header row is 5px taller */,
        }}
        ref={scrollContainerRef}
      >
        <Table aria-labelledby="tableTitle" size="small" aria-label="enhanced table" stickyHeader={true}>
          <EnhancedTableHead<IItem>
            columns={columns}
            order={order}
            toggleOrderOf={toggleOrderOf}
            selection={selection}
            disableSorting={isServerMode}
          />
          <TableBody>
            {displayItems.map((item, index) => {
              const isItemSelected = selection ? selection.isSelected(item.id) : false;
              const labelId = `enhanced-table-checkbox-${index}`;

              const multiSelection = selection && selection.multi;

              return (
                <TableRow
                  key={item.id}
                  hover
                  onClick={selection ? () => selection.selectOnly(item.id) : void 0}
                  {...(selection
                    ? {
                        role: 'checkbox',
                        'aria-checked': isItemSelected,
                      }
                    : {})}
                  tabIndex={-1}
                  selected={isItemSelected}
                >
                  {multiSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        slotProps={{ input: { 'aria-labelledby': labelId } }}
                        onClick={(event) => {
                          event.stopPropagation();
                          multiSelection.toggle(item.id);
                        }}
                      />
                    </TableCell>
                  )}
                  {columns.slice(0, 1).map((column) => (
                    <TableCell
                      key="title-cell"
                      component="th"
                      id={labelId}
                      scope="row"
                      padding={multiSelection ? 'none' : 'normal'}
                    >
                      {renderCell(item, column)}
                    </TableCell>
                  ))}
                  {columns.slice(1).map((column) => (
                    <TableCell key={String(column.key)} align="left">
                      {renderCell(item, column)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: rowHeightPx * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
}

function renderCell<IItem>(item: IItem, column: IColumnDefinition<IItem>): ReactNode {
  if (typeof column.render === 'function') {
    return column.render(item);
  } else {
    return String(item[column.key]);
  }
}
